// @flow

import { useMemo } from 'react';

import { selectTaxesDisplaySetting } from '@gandi/authentication';

import { useAppSelector } from 'src/hooks/useAppSelector';
import type { ProductSummary } from 'src/modules/mailbox-standalone/resources/products';

import type { Props as PriceProps } from '../../components/Price';

import { useProductsFetch } from './useProductsFetch';

/** Returns price info formatted for the `<Price />` component,
 * given a catalog product, the catalog duration to retrieve,
 * and the duration used for the final computed price.
 *
 * @returns props that can be directly spread as `<Price {...props} />`.
 */
export function useProductPrice({
  product,
  // Monthly display by default
  duration = 1,
  // Yearly catalog by default
  minDuration = 12,
}: {
  product: ProductSummary,
  /** The final duration used to show the price */
  duration?: number,
  /** The billing periodicity used to fetch the prices */
  minDuration?: number,
}): void | PriceProps {
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);

  const price = product.prices.find(
    p => p.min_duration <= minDuration && p.max_duration >= minDuration,
  );
  if (!price) {
    return undefined;
  }

  const currency = price.currency;
  const hasTaxes = product.taxes.some(tax => tax.rate !== 0);

  const amount = displayTaxes
    ? price.price_after_taxes
    : price.price_before_taxes;

  let priceCrossedOut = displayTaxes
    ? price.normal_price_after_taxes
    : price.normal_price_before_taxes;
  if (priceCrossedOut) priceCrossedOut *= duration;

  return {
    currency,
    price: amount * duration,
    priceCrossedOut,
    showExcludeVatMention: hasTaxes && !displayTaxes,
  };
}

/** Returns the price (excluding taxes) for a standalone mailbox based on its type and duration. */
export function useStandaloneMailboxPrice({
  mailboxType = 'standard',
  duration = 1,
  durationUnit = 'month',
  shouldFetch = false,
}: {
  mailboxType?: string,
  duration?: number,
  durationUnit?: string,
  shouldFetch?: boolean,
}): number | void {
  const { summaries } = useProductsFetch({ enabled: shouldFetch });

  return useMemo((): number | void => {
    if (!summaries || !mailboxType) return undefined;

    const catalogProduct = summaries.find(p => p.config_name === mailboxType);

    const targetPrice = catalogProduct?.prices?.find(
      p =>
        p.duration_unit === durationUnit &&
        p.min_duration <= duration &&
        p.max_duration >= duration,
    );

    return targetPrice?.price_before_taxes;
  }, [summaries, mailboxType, duration, durationUnit]);
}
